import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { resolveTimezoneName } from '../../utils/time';
import { escapeHtml, safeColor } from '../../utils/html';

interface BoardConfig {
  name: string; show_address: boolean; show_weather: boolean;
  show_map: boolean; show_map_units: boolean;
  history_hours: number;
  disciplines: string[]; agency_name: string; agency_logo: string; timezone: string;
  map_center_lat: number; map_center_lng: number;
}
interface Summary { calls_today: number; calls_last_7_days: number; open_now: number; }
interface OpenCall {
  id: number; cad_number: string; call_type_code: string; call_type_name: string;
  priority: number; status: string; location: string; created_at: string; units: string[];
  lat: number | null; lng: number | null;
}
interface BoardUnit {
  id: number; callsign: string; status_name: string; status_color: string;
  lat: number; lng: number;
}
interface HistoryCall extends OpenCall {
  disposition: string; closed_at: string | null;
}
interface WeatherAlert { event: string; severity: string; headline: string; }
interface Weather {
  available: boolean; temp_f: number | null; conditions: string;
  wind_mph: number | null; alerts: WeatherAlert[];
}

const PRIORITY_COLORS: Record<number, string> = {
  1: '#f44336', 2: '#ff9800', 3: '#2196f3', 4: '#4caf50', 5: '#9e9e9e',
};

function priorityColor(p: number): string {
  return PRIORITY_COLORS[p] || '#2196f3';
}

/** Backend sends naive ISO strings for stored (UTC) timestamps. */
function utcDate(iso: string): Date {
  const hasZone = iso.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(iso);
  return new Date(hasZone ? iso : `${iso}Z`);
}

/** 24-hour HH:MM in the tenant's timezone. */
function clockTime(iso: string, tz: string): string {
  try {
    return utcDate(iso).toLocaleTimeString('en-GB', {
      hour12: false, hour: '2-digit', minute: '2-digit', timeZone: tz,
    });
  } catch {
    return utcDate(iso).toLocaleTimeString('en-GB', { hour12: false, hour: '2-digit', minute: '2-digit' });
  }
}

/** Wall clock: short date + 24-hour time with zone label, in the tenant's tz. */
function wallClock(tz: string): string {
  const d = new Date();
  const opts = (o: Intl.DateTimeFormatOptions): Intl.DateTimeFormatOptions => {
    try {
      new Date().toLocaleString('en-GB', { timeZone: tz });
      return { ...o, timeZone: tz };
    } catch {
      return o;
    }
  };
  const date = d.toLocaleDateString('en-US', opts({ weekday: 'short', month: 'short', day: 'numeric' }));
  // en-US + h23 so the zone reads as an abbreviation (CDT) rather than GMT-5.
  const time = d.toLocaleTimeString('en-US', opts({
    hourCycle: 'h23', hour: '2-digit', minute: '2-digit', second: '2-digit', timeZoneName: 'short',
  }));
  return `${date}  ${time}`;
}

function elapsed(iso: string, now: number): string {
  const start = utcDate(iso).getTime();
  let s = Math.max(0, Math.floor((now - start) / 1000));
  const h = Math.floor(s / 3600); s -= h * 3600;
  const m = Math.floor(s / 60); s -= m * 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

export default function BoardPage() {
  const { token } = useParams<{ token: string }>();
  const [config, setConfig] = useState<BoardConfig | null>(null);
  const [summary, setSummary] = useState<Summary>({ calls_today: 0, calls_last_7_days: 0, open_now: 0 });
  const [calls, setCalls] = useState<OpenCall[]>([]);
  const [history, setHistory] = useState<HistoryCall[]>([]);
  const [weather, setWeather] = useState<Weather | null>(null);
  const [units, setUnits] = useState<BoardUnit[]>([]);
  const [error, setError] = useState('');
  const [now, setNow] = useState(Date.now());
  const [clock, setClock] = useState('');
  const wsRef = useRef<WebSocket | null>(null);

  const base = `/api/board/${token}`;
  const tz = resolveTimezoneName(config?.timezone || 'UTC');

  const refresh = useCallback(async () => {
    try {
      const [s, c] = await Promise.all([
        fetch(`${base}/summary`),
        fetch(`${base}/open-calls`),
      ]);
      if (s.status === 404 || c.status === 404) { setError('This board link is invalid or has been revoked.'); return; }
      setError('');
      if (s.ok) setSummary(await s.json());
      if (c.ok) setCalls(await c.json());
    } catch {
      /* keep last-known data on transient errors */
    }
  }, [base]);

  const refreshHistory = useCallback(async () => {
    try {
      const r = await fetch(`${base}/history`);
      if (r.ok) setHistory(await r.json());
    } catch { /* ignore */ }
  }, [base]);

  const refreshUnits = useCallback(async () => {
    try {
      const r = await fetch(`${base}/units`);
      if (r.ok) setUnits(await r.json());
    } catch { /* ignore */ }
  }, [base]);

  const refreshWeather = useCallback(async () => {
    try {
      const r = await fetch(`${base}/weather`);
      if (r.ok) setWeather(await r.json());
    } catch { /* ignore */ }
  }, [base]);

  // Initial config load.
  useEffect(() => {
    fetch(`${base}`).then((r) => {
      if (r.status === 404) { setError('This board link is invalid or has been revoked.'); return null; }
      return r.ok ? r.json() : null;
    }).then((cfg) => { if (cfg) setConfig(cfg); }).catch(() => setError('Unable to reach the server.'));
  }, [base]);

  // Data polling + weather polling.
  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 15000);
    return () => clearInterval(id);
  }, [refresh]);

  const historyEnabled = !!config && config.history_hours > 0;
  const historyEnabledRef = useRef(false);
  useEffect(() => { historyEnabledRef.current = historyEnabled; }, [historyEnabled]);
  useEffect(() => {
    if (!historyEnabled) { setHistory([]); return; }
    refreshHistory();
    const id = setInterval(refreshHistory, 30000);
    return () => clearInterval(id);
  }, [historyEnabled, refreshHistory]);

  const mapEnabled = !!config?.show_map;
  const unitsEnabled = !!config?.show_map_units;
  const unitsEnabledRef = useRef(false);
  useEffect(() => { unitsEnabledRef.current = unitsEnabled; }, [unitsEnabled]);
  useEffect(() => {
    if (!unitsEnabled) { setUnits([]); return; }
    refreshUnits();
    const id = setInterval(refreshUnits, 15000);
    return () => clearInterval(id);
  }, [unitsEnabled, refreshUnits]);

  useEffect(() => {
    refreshWeather();
    const id = setInterval(refreshWeather, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [refreshWeather]);

  // Live WebSocket (board-token connection) with reconnect + poll fallback.
  useEffect(() => {
    let closed = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    const connect = () => {
      if (closed) return;
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const ws = new WebSocket(`${protocol}//${window.location.host}/api/ws?mode=board&board=${encodeURIComponent(token || '')}`);
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (['call_created', 'call_updated', 'unit_cleared', 'call_deleted'].includes(msg.event)) {
            refresh();
            if (historyEnabledRef.current) refreshHistory();
          }
          if (unitsEnabledRef.current
              && ['unit_location_updated', 'unit_updated', 'unit_status_changed'].includes(msg.event)) {
            refreshUnits();
          }
        } catch { /* ignore */ }
      };
      ws.onclose = () => {
        if (!closed) reconnectTimer = setTimeout(connect, 5000);
      };
      ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
    };
    connect();
    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { wsRef.current?.close(); } catch { /* ignore */ }
    };
  }, [token, refresh, refreshHistory, refreshUnits]);

  // 1s tick for elapsed timers + wall clock.
  useEffect(() => {
    const tick = () => {
      setNow(Date.now());
      setClock(wallClock(tz));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [tz]);

  if (error) {
    return (
      <div style={{ ...pageStyle, alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
        <div>
          <div style={{ fontSize: 48, fontWeight: 800, color: '#f44336' }}>Board Unavailable</div>
          <div style={{ fontSize: 24, color: '#bbb', marginTop: 16 }}>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          {config?.agency_logo ? <img src={config.agency_logo} alt="" style={{ height: 56 }} /> : null}
          <div>
            <div style={{ fontSize: 34, fontWeight: 800 }}>{config?.name || 'Status Board'}</div>
            <div style={{ fontSize: 18, color: '#9e9e9e' }}>{config?.agency_name || ''}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
          {config?.show_weather && weather?.available ? (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 40, fontWeight: 800 }}>
                {weather.temp_f != null ? `${Math.round(weather.temp_f)}°F` : '--'}
              </div>
              <div style={{ fontSize: 18, color: '#bbb' }}>
                {weather.conditions}{weather.wind_mph != null ? ` · ${Math.round(weather.wind_mph)} mph` : ''}
              </div>
            </div>
          ) : null}
          <div style={{ fontSize: 26, fontWeight: 700, color: '#e0e0e0', minWidth: 300, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{clock}</div>
        </div>
      </div>

      {/* Weather alerts strip */}
      {config?.show_weather && weather?.alerts && weather.alerts.length > 0 ? (
        <div style={alertStripStyle}>
          {weather.alerts.slice(0, 3).map((a, i) => (
            <span key={i} style={{ marginRight: 28 }}>
              ⚠ {a.event}{a.headline ? ` — ${a.headline}` : ''}
            </span>
          ))}
        </div>
      ) : null}

      {/* KPI tiles */}
      <div style={kpiRowStyle}>
        <Kpi label="Calls Today" value={summary.calls_today} color="#2196f3" />
        <Kpi label="Calls Last 7 Days" value={summary.calls_last_7_days} color="#00bcd4" />
        <Kpi label="Open Now" value={summary.open_now} color="#ff9800" />
      </div>

      {/* Open calls (+ map) */}
      <div style={{ flex: historyEnabled ? 2 : 1, overflow: 'hidden', padding: '0 24px 24px', display: 'flex', gap: 20 }}>
        <div style={{ flex: mapEnabled ? 1.3 : 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#9e9e9e', margin: '4px 0 10px' }}>ACTIVE CALLS</div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {calls.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#666', fontSize: 28, marginTop: 60 }}>No active calls</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 22 }}>
                <thead>
                  <tr style={{ color: '#9e9e9e', fontSize: 16, textAlign: 'left' }}>
                    <th style={thStyle}>Pri</th>
                    <th style={thStyle}>CAD #</th>
                    <th style={thStyle}>Type</th>
                    {config?.show_address ? <th style={thStyle}>Location</th> : null}
                    <th style={thStyle}>Units</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Elapsed</th>
                  </tr>
                </thead>
                <tbody>
                  {calls.map((c) => (
                    <tr key={c.id} style={{ borderBottom: '1px solid #2a2a2a' }}>
                      <td style={tdStyle}>
                        <span style={{
                          display: 'inline-block', minWidth: 34, textAlign: 'center', borderRadius: 6,
                          background: priorityColor(c.priority), color: '#fff', fontWeight: 800, padding: '2px 8px',
                        }}>{c.priority}</span>
                      </td>
                      <td style={{ ...tdStyle, fontWeight: 700 }}>{c.cad_number}</td>
                      <td style={tdStyle}>{c.call_type_name || c.call_type_code || '—'}</td>
                      {config?.show_address ? <td style={tdStyle}>{c.location || '—'}</td> : null}
                      <td style={{ ...tdStyle, color: '#8ecaff' }}>{c.units.length ? c.units.join(', ') : '—'}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{elapsed(c.created_at, now)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
        {mapEnabled ? (
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#9e9e9e', margin: '4px 0 10px' }}>MAP</div>
            <div style={{ flex: 1, borderRadius: 12, overflow: 'hidden', border: '1px solid #222' }}>
              <BoardMap
                calls={calls}
                units={units}
                centerLat={config?.map_center_lat ?? 39.8283}
                centerLng={config?.map_center_lng ?? -98.5795}
              />
            </div>
          </div>
        ) : null}
      </div>

      {/* Recent call history */}
      {historyEnabled ? (
        <div style={{ flex: 1, overflow: 'hidden', padding: '0 24px 24px', display: 'flex', flexDirection: 'column', borderTop: '1px solid #222' }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#9e9e9e', margin: '10px 0 10px' }}>
            RECENT CALLS · LAST {config?.history_hours}H
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {history.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#666', fontSize: 24, marginTop: 30 }}>No recent calls</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 20 }}>
                <thead>
                  <tr style={{ color: '#9e9e9e', fontSize: 15, textAlign: 'left' }}>
                    <th style={thStyle}>Pri</th>
                    <th style={thStyle}>CAD #</th>
                    <th style={thStyle}>Type</th>
                    {config?.show_address ? <th style={thStyle}>Location</th> : null}
                    <th style={thStyle}>Disposition</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Closed</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((c) => (
                    <tr key={c.id} style={{ borderBottom: '1px solid #222', color: '#bdbdbd' }}>
                      <td style={tdStyle}>
                        <span style={{
                          display: 'inline-block', minWidth: 30, textAlign: 'center', borderRadius: 6,
                          background: priorityColor(c.priority), color: '#fff', fontWeight: 700, padding: '2px 7px', opacity: 0.75,
                        }}>{c.priority}</span>
                      </td>
                      <td style={{ ...tdStyle, fontWeight: 700 }}>{c.cad_number}</td>
                      <td style={tdStyle}>{c.call_type_name || c.call_type_code || '—'}</td>
                      {config?.show_address ? <td style={tdStyle}>{c.location || '—'}</td> : null}
                      <td style={tdStyle}>{c.disposition || (c.status === 'cancelled' ? 'Cancelled' : '—')}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {c.closed_at ? clockTime(c.closed_at, tz) : clockTime(c.created_at, tz)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Leaflet map of the board's active calls (and, optionally, logged-on units). */
function BoardMap({ calls, units, centerLat, centerLng }: {
  calls: OpenCall[]; units: BoardUnit[]; centerLat: number; centerLng: number;
}) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerLayerRef = useRef<any>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const ensureLeaflet = (cb: () => void) => {
      const L = (window as any).L;
      if (L) { cb(); return; }
      if (!document.querySelector('link[data-leaflet]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        link.setAttribute('data-leaflet', '1');
        document.head.appendChild(link);
      }
      let script = document.querySelector('script[data-leaflet]') as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.setAttribute('data-leaflet', '1');
        document.head.appendChild(script);
      }
      script.addEventListener('load', cb);
    };

    ensureLeaflet(() => {
      const L = (window as any).L;
      if (!L || !mapRef.current || mapInstanceRef.current) return;
      // Unattended screen: no zoom/pan controls to get left in a bad state.
      const map = L.map(mapRef.current, {
        maxZoom: 19, zoomControl: false, attributionControl: false,
        dragging: false, scrollWheelZoom: false, doubleClickZoom: false,
        boxZoom: false, keyboard: false, touchZoom: false,
      }).setView([centerLat, centerLng], 12);
      L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
        maxZoom: 19, attribution: '&copy; OpenStreetMap &copy; CARTO',
      }).addTo(map);
      mapInstanceRef.current = map;
      setTimeout(() => map.invalidateSize(), 200);
      setReady(true);
    });

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const L = (window as any).L;
    const map = mapInstanceRef.current;
    if (!L || !map || !ready) return;

    if (markerLayerRef.current) map.removeLayer(markerLayerRef.current);
    const group = L.layerGroup().addTo(map);
    markerLayerRef.current = group;

    const pts: [number, number][] = [];

    calls.forEach((c) => {
      if (c.lat == null || c.lng == null) return;
      const color = priorityColor(c.priority);
      const icon = L.divIcon({
        className: '',
        html: `<div style="text-align:center">`
          + `<div style="background:${color};color:#fff;border-radius:50%;width:30px;height:30px;`
          + `display:flex;align-items:center;justify-content:center;font-weight:800;font-size:15px;`
          + `border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.6);margin:0 auto">${escapeHtml(c.priority)}</div>`
          + `<div style="color:#fff;font-size:13px;font-weight:700;text-shadow:0 1px 3px #000;white-space:nowrap">${escapeHtml(c.cad_number)}</div>`
          + `</div>`,
        iconSize: [0, 0],
        iconAnchor: [15, 15],
      });
      L.marker([c.lat, c.lng], { icon }).addTo(group);
      pts.push([c.lat, c.lng]);
    });

    units.forEach((u) => {
      const icon = L.divIcon({
        className: '',
        html: `<div style="background:${safeColor(u.status_color)};color:#fff;padding:2px 7px;border-radius:4px;`
          + `font-size:12px;font-weight:700;white-space:nowrap;border:1px solid #fff;`
          + `box-shadow:0 1px 3px rgba(0,0,0,.5)">${escapeHtml(u.callsign)}</div>`,
        iconSize: [0, 0],
        iconAnchor: [0, 0],
      });
      L.marker([u.lat, u.lng], { icon }).addTo(group);
      pts.push([u.lat, u.lng]);
    });

    if (pts.length > 1) {
      map.fitBounds(pts, { padding: [60, 60], maxZoom: 16 });
    } else if (pts.length === 1) {
      map.setView(pts[0], 15);
    } else {
      map.setView([centerLat, centerLng], 12);
    }
  }, [calls, units, centerLat, centerLng, ready]);

  return <div ref={mapRef} style={{ width: '100%', height: '100%', background: '#111' }} />;
}

function Kpi({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div style={{ flex: 1, background: '#1a1a1a', borderRadius: 12, padding: '18px 24px', borderTop: `4px solid ${color}` }}>
      <div style={{ fontSize: 20, color: '#9e9e9e', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 64, fontWeight: 800, lineHeight: 1.1, color }}>{value}</div>
    </div>
  );
}

const pageStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: '#0d0d0d', color: '#fff',
  display: 'flex', flexDirection: 'column', fontFamily: '"Roboto", "Helvetica", Arial, sans-serif',
};
const headerStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  padding: '18px 24px', borderBottom: '1px solid #222',
};
const alertStripStyle: React.CSSProperties = {
  background: '#7f1d1d', color: '#fff', fontSize: 20, fontWeight: 700,
  padding: '8px 24px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
};
const kpiRowStyle: React.CSSProperties = { display: 'flex', gap: 20, padding: 24 };
const thStyle: React.CSSProperties = { padding: '6px 12px', fontWeight: 700, borderBottom: '2px solid #333' };
const tdStyle: React.CSSProperties = { padding: '10px 12px' };
